import createReducer from "./createReducer";
import { LIBRARY_ACTIONS } from "./LibraryProperties";

import { addFiltering } from "./common/Filtering";
import { addColumnFilters } from "./common/ColumnFilters";
import { SUPPRESS_SUCCESS_GROWL } from "utils/constants";
import { escapeUnderscoreForAPI } from "utils/common";

export const LIBRARIES_ACTIONS = {
	LOAD_LIBRARIES_AJAX: "LOAD_LIBRARIES_AJAX",
	LOAD_LIBRARIES_SUCCESS: "LOAD_LIBRARIES_SUCCESS",
	LOAD_LIBRARIES_ERROR: "LOAD_LIBRARIES_ERROR",
	LOAD_LIBRARIES_CANCEL: "LOAD_LIBRARIES_CANCEL",

	LOAD_LIBRARIES_DISTINCTS_AJAX: "LOAD_LIBRARIES_DISTINCTS_AJAX",
	LOAD_LIBRARIES_DISTINCTS_SUCCESS: "LOAD_LIBRARIES_DISTINCTS_SUCCESS",
	LOAD_LIBRARIES_DISTINCTS_ERROR: "LOAD_LIBRARIES_DISTINCTS_ERROR",
};

const initialLibrariesState = {
	success: true,
	items: [],

	totalPages: 1,
	totalItems: 0,

	isLoaded: false,
	hasErrorLoading: false,

	isDistinctsLoaded: false,
	hasErrorLoadingDistincts: false,
};

const handlers = {
	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_AJAX]: isLoadingLibraries,
	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_SUCCESS]: initLibraries,
	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_ERROR]: initErrorLibraries,

	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_AJAX]: isLoadingLibrariesDistincts,
	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_SUCCESS]: initLibrariesDistincts,
	[LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_ERROR]: initErrorLibrariesDistincts,

	[LIBRARY_ACTIONS.CREATE_LIBRARY_SUCCESS]: addLibrary,
	[LIBRARY_ACTIONS.UPDATE_LIBRARY_SUCCESS]: updateLibrary,

	[LIBRARY_ACTIONS.DELETE_LIBRARY_AJAX]: startDeleting,
	[LIBRARY_ACTIONS.DELETE_LIBRARY_SUCCESS]: deleteLibrarySuccess,
};

let reducerConfig = {
	typeRoot: "LIBRARIES",
	actionTypes: LIBRARIES_ACTIONS,
	actionCreators: {
		loadLibraries,
		loadDistincts,
	},
	initialState: initialLibrariesState,
	handlers,
};

reducerConfig = addFiltering(reducerConfig, { sort: "id:DESC" });
reducerConfig = addColumnFilters(reducerConfig, {
	advertiserName: {
		name: "advertiserName",
		type: "String",
		displayName: "Advertiser",
	},
	mediaBuyerName: {
		name: "mediaBuyerName",
		type: "String",
		displayName: "Media Buyer",
	},
	mediaBuyerEmail: {
		name: "mediaBuyerEmail",
		type: "String",
		displayName: "Media Buyer E-mail",
	},
	email: {
		name: "email",
		type: "String",
		displayName: "Primary Contact",
	},
	brand: {
		name: "brand",
		type: "String",
		displayName: "Brand",
	},
	qaLocation: {
		name: "qaLocation",
		type: "Checkbox",
		displayName: "Territory",
		dictionaryName: "qaLocationId",
	},
	dateCreated: {
		name: "dateCreated",
		type: "Date",
		displayName: "Date Created",
	},
});

const { updateColumnFilter } = reducerConfig.actionCreators;

export function loadLibraries(cancelToken){
	return (dispatch, getState) => {
		let state = getState();
		let { filterInfo = {} } = state.libraries;
		const params = { ...filterInfo };

		if (params.searchTerm) {
			params.searchTerm = escapeUnderscoreForAPI(filterInfo.searchTerm);
		}

		return dispatch({
			type: LIBRARIES_ACTIONS.LOAD_LIBRARIES_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX:{
				url: "creative-libraries",
				params,
				success: LIBRARIES_ACTIONS.LOAD_LIBRARIES_SUCCESS,
				error: LIBRARIES_ACTIONS.LOAD_LIBRARIES_ERROR,
				cancel: LIBRARIES_ACTIONS.LOAD_LIBRARIES_CANCEL,
				cancelToken,
			},
		});
	};
}

export function loadDistincts(columnNameList){
	/*
		Fetches distinct values for the provided creativelibraries column names,
		and loads them as options for the corresponding columnFilters,
		i.e. for Checkbox filters.
	*/
	let commaSeparatedColumnNames = columnNameList.join(",");
	return (dispatch, getState) => {
		return dispatch({
			type: LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_AJAX,
			meta:SUPPRESS_SUCCESS_GROWL,
			AJAX: {
				url: "creative-libraries/distincts",
				params: { distincts: commaSeparatedColumnNames },
				success: LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_SUCCESS,
				error: LIBRARIES_ACTIONS.LOAD_LIBRARIES_DISTINCTS_ERROR,
			},
		}).then((response) => {
			if(response.success){
				let { data } = response.payload;
				let keys = Object.keys(data);

				keys.forEach((key) => {
					let distincts = data[key];
					/*
						The server response usually contains a null value, and an empty string,
						which we're not interested in.

						We can trim them out here, rather than have to add logic to ignore them
						every time we want to work with these values.
					*/
					let filteredDistincts = distincts.filter(item => item);

					// Patch the options into the corresponding columnFilters.
					dispatch(updateColumnFilter(key, {
						options: filteredDistincts,
					}));
				});
			}
		});
	};
}

function addLibrary(state, action){
	let { items } = state;
	let newLibrary = action.payload.data;
	return {
		...state,
		items: [newLibrary, ...items],
	};
}

function updateLibrary(state, action){
	// Replace the item(s?) if it's in the current list.
	let { items } = state;
	let updatedLibrary = action.payload.data;

	let replacedItems = items.map((library) => {
		return (library.id === updatedLibrary.id) ? updatedLibrary : library;
	});

	return {
		...state,
		items: replacedItems,
	};
}

function startDeleting(state, action){
	return {
		...state,
	};
}

function deleteLibrarySuccess(state, action){
	let { id } = action.payload.original;
	let { items } = state;
	let intID = parseInt(id); // id arguments are likely to come from route paths, which will be strings.

	// Remove the deleted library from the list of items
	let filteredLibraries = items.filter(library =>  (library.id !== intID));

	return {
		...state,
		items: filteredLibraries,
	};
}

function isLoadingLibraries(state, action){
	return {
		...state,
		isLoaded: false,
	};
}

function initLibraries(state, action){
	let { data } = action.payload;
	return {
		...state,
		...data,
		isLoaded: true,
		hasErrorLoading: false,
	};
}

function initErrorLibraries(state, action){
	return {
		...state,
		isLoaded: true,
		hasErrorLoading: true,
	};
}

function isLoadingLibrariesDistincts(state, action){
	return {
		...state,
		isDistinctsLoaded: false,
	};
}

function initLibrariesDistincts(state, action){
	return {
		...state,
		isDistinctsLoaded: true,
		hasErrorLoadingDistincts: false,
	};
}

function initErrorLibrariesDistincts(state, action){
	return {
		...state,
		isDistinctsLoaded: true,
		hasErrorLoadingDistincts: true,
	};
}


export default createReducer(reducerConfig.initialState, reducerConfig.handlers);
export const Libraries = reducerConfig.actionCreators;
